import React, { createContext, useContext, useState, ReactNode, ReactElement } from "react";

interface ModalProps {
    targetRoute?: string;
    onClose: () => void;
}

export interface BlockerConfig {
    condition: boolean;
    modal: ReactElement<ModalProps>;
}

interface NavigationBlockerContextType {
    globalBlockers: BlockerConfig[];
    setGlobalBlockers: (blockers: BlockerConfig[]) => void;
    showBlocker: (targetRoute?: string) => boolean;
    clearBlocker: () => void;
    activeModal: ReactNode | null;
}

const NavigationBlockerContext = createContext<NavigationBlockerContextType>({
    globalBlockers: [],
    setGlobalBlockers: () => {},
    showBlocker: () => false,
    clearBlocker: () => {},
    activeModal: null,
});

export const NavigationBlockerProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const [globalBlockers, setGlobalBlockers] = useState<BlockerConfig[]>([]);
    const [activeModal, setActiveModal] = useState<ReactNode | null>(null);

    const showBlocker = (targetRoute?: string) => {
        const blocker = globalBlockers.find(b => b.condition);
        if (blocker) {
            const modal = React.isValidElement(blocker.modal)
                ? React.cloneElement(blocker.modal, { targetRoute })
                : blocker.modal;
            setActiveModal(modal);
            return true;
        }
        return false;
    };

    const clearBlocker = () => {
        setActiveModal(null);
    };

    return (
        <NavigationBlockerContext.Provider value={{ globalBlockers, setGlobalBlockers, showBlocker, clearBlocker, activeModal }}>
            {children}
            {activeModal}
        </NavigationBlockerContext.Provider>
    );
};

export const useNavigationBlocker = () => useContext(NavigationBlockerContext);
